// Tłumaczy typowe komunikaty błędów z backendu (class-validator, Prisma, custom) na polski.
// Wywołanie: translateError(e) zwraca jeden gotowy string.
// Używać w catch-ach: Swal.fire('Błąd', translateError(e), 'error').

// Mapa pól po angielsku → po polsku (do podstawienia w komunikatach class-validator).
const FIELD_MAP = {
  email: 'adres e-mail',
  phone: 'telefon',
  password: 'hasło',
  firstName: 'imię',
  lastName: 'nazwisko',
  pesel: 'PESEL',
  nip: 'NIP',
  regon: 'REGON',
  name: 'nazwa',
  description: 'opis',
  amount: 'kwota',
  price: 'cena',
  rentPrice: 'cena najmu',
  advance: 'zaliczka',
  odometer: 'przebieg',
  previousServiceOdometer: 'przebieg z ostatniego serwisu',
  productionYear: 'rok produkcji',
  plate: 'numer rejestracyjny',
  vin: 'numer VIN',
  make: 'marka',
  model: 'model',
  color: 'kolor',
  cardId: 'karta paliwowa',
  driverId: 'kierowca',
  carId: 'pojazd',
  date: 'data',
  startDate: 'data rozpoczęcia',
  endDate: 'data zakończenia',
  status: 'status',
  category: 'kategoria',
  city: 'miasto',
  street: 'ulica',
  zipCode: 'kod pocztowy',
  bankAccount: 'numer konta',
  iban: 'IBAN',
};

function pl(name) {
  return FIELD_MAP[name] || name;
}

const PATTERNS = [
  // class-validator
  { re: /^(\w+) must be a string$/i, fn: m => `Pole "${pl(m[1])}" musi być tekstem` },
  { re: /^(\w+) must be a number(.+)?$/i, fn: m => `Pole "${pl(m[1])}" musi być liczbą` },
  { re: /^(\w+) must be an integer$/i, fn: m => `Pole "${pl(m[1])}" musi być liczbą całkowitą` },
  { re: /^(\w+) must be a boolean(.+)?$/i, fn: m => `Pole "${pl(m[1])}" musi być wartością tak/nie` },
  { re: /^(\w+) must be an? (?:UUID|uuid)$/i, fn: m => `Pole "${pl(m[1])}" musi być prawidłowym UUID` },
  { re: /^(\w+) must be a valid ISO ?8601 date( string)?$/i, fn: m => `Pole "${pl(m[1])}" musi być prawidłową datą` },
  { re: /^(\w+) must be a valid enum value$/i, fn: m => `Pole "${pl(m[1])}" zawiera niedozwoloną wartość` },
  { re: /^(\w+) must be an email$/i, fn: m => `Pole "${pl(m[1])}" musi być prawidłowym adresem e-mail` },
  { re: /^(\w+) should not be empty$/i, fn: m => `Pole "${pl(m[1])}" nie może być puste` },
  { re: /^(\w+) must be longer than or equal to (\d+) characters?$/i, fn: m => `Pole "${pl(m[1])}" musi mieć co najmniej ${m[2]} znaków` },
  { re: /^(\w+) must be shorter than or equal to (\d+) characters?$/i, fn: m => `Pole "${pl(m[1])}" może mieć maksymalnie ${m[2]} znaków` },
  { re: /^(\w+) must not be less than (-?\d+(?:\.\d+)?)$/i, fn: m => `Pole "${pl(m[1])}" nie może być mniejsze niż ${m[2]}` },
  { re: /^(\w+) must not be greater than (-?\d+(?:\.\d+)?)$/i, fn: m => `Pole "${pl(m[1])}" nie może być większe niż ${m[2]}` },
  { re: /property (\w+) should not exist/i, fn: m => `Niedozwolone pole "${pl(m[1])}"` },
  { re: /^Validation failed/i, fn: () => 'Niepoprawne dane formularza' },

  // Prisma
  { re: /Unique constraint failed on the fields?: \(?`?(\w+)`?\)?/i, fn: m => `Wartość pola "${pl(m[1])}" już istnieje w bazie` },
  { re: /Foreign key constraint failed/i, fn: () => 'Powiązany rekord nie istnieje lub nie może zostać użyty' },
  { re: /Record to (?:update|delete) (?:does )?not found/i, fn: () => 'Rekord nie został znaleziony' },
  { re: /Argument `(\w+)` is missing/i, fn: m => `Brak wymaganego pola "${pl(m[1])}"` },

  // Auth / generic
  { re: /Unauthorized/i, fn: () => 'Brak autoryzacji' },
  { re: /Forbidden/i, fn: () => 'Brak uprawnień do tej operacji' },
  { re: /Not Found/i, fn: () => 'Nie znaleziono zasobu' },
  { re: /Bad Request/i, fn: () => 'Nieprawidłowe żądanie' },
  { re: /Internal server error/i, fn: () => 'Błąd serwera — spróbuj ponownie' },
  { re: /Network Error/i, fn: () => 'Brak połączenia z serwerem' },
];

function translateOne(line) {
  if (!line) return '';
  const s = String(line).trim();
  for (const { re, fn } of PATTERNS) {
    const m = s.match(re);
    if (m) return fn(m);
  }
  return s; // fallback — bez tłumaczenia
}

/**
 * Główny entry point: weź `error` z catch, wyciągnij komunikat (lub listę) i przetłumacz.
 */
export function translateError(err) {
  // Brak response (np. network error)
  if (!err) return 'Nieznany błąd';
  if (err.message === 'Network Error' || (!err.response && err.message)) {
    return translateOne(err.message);
  }
  const data = err.response && err.response.data;
  if (!data) return translateOne(err.message);

  // Backend zwraca albo string, albo array, albo {message, statusCode}
  let raw = data.message ?? data.error ?? data;
  if (typeof raw === 'string') return translateOne(raw);
  if (Array.isArray(raw)) {
    return raw.map(translateOne).filter(Boolean).join(' • ');
  }
  if (typeof raw === 'object') return translateOne(JSON.stringify(raw));
  return translateOne(String(raw));
}

export default translateError;
